import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';

/**
 * Data structure for LocationListRow component
 */
export interface LocationListRowData {
  id: string;
  type: 'physical' | 'virtual';
  name: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
}

/**
 * LocationListRow Component
 *
 * Displays a location's information in a horizontal row format for list views.
 * Shows color dot, icon, name, type badge, description, and action icons.
 *
 * @example
 * ```html
 * <zag-location-list-row
 *   [location]="locationData"
 *   [editRoute]="['/locations', locationData.id]"
 *   (rowClick)="onLocationSelected($event)"
 *   (deleteClick)="onDeleteLocation($event)">
 * </zag-location-list-row>
 * ```
 */
@Component({
  selector: 'zag-location-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon],
  host: {
    '[class.zag-location-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <div class="zag-location-list-row__content">
      <div
        class="zag-location-list-row__icon-avatar zag-icon-surface-gradient"
        [class.zag-location-list-row__icon-avatar--fallback]="!location().color"
        [style.background-color]="location().color || null"
      >
        <span
          class="zag-location-list-row__icon"
          [zagIcon]="location().icon || 'location_on'"
          [style.color]="getIconColor(location().color)"
        ></span>
      </div>
      <div class="zag-location-list-row__info">
        <div class="zag-location-list-row__header">
          <span class="zag-location-list-row__name">{{ location().name }}</span>
          <span
            class="zag-location-list-row__type-badge"
            [class.zag-location-list-row__type-badge--physical]="location().type === 'physical'"
            [class.zag-location-list-row__type-badge--virtual]="location().type === 'virtual'"
          >
            {{ location().type }}
          </span>
        </div>
        @if (location().description) {
          <span class="zag-location-list-row__description">{{ location().description }}</span>
        }
      </div>
    </div>

    <div class="zag-location-list-row__actions">
      @if (showDelete()) {
        <button
          type="button"
          class="zag-location-list-row__action zag-location-list-row__action--delete"
          [disabled]="deleteDisabled()"
          [attr.aria-label]="deleteAriaLabel() || null"
          (click)="onDeleteClick($event)"
        >
          <span zagIcon="delete"></span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .zag-location-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-location-list-row__content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        min-width: 0;
      }

      .zag-location-list-row__icon {
        font-size: var(--entity-avatar-icon-size-list);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .zag-location-list-row__icon-avatar {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .zag-location-list-row__icon-avatar--fallback {
        background: linear-gradient(135deg, var(--color-primary), var(--color-primary-variant));
      }

      .zag-location-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-location-list-row__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
      }

      .zag-location-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-location-list-row__type-badge {
        font-size: var(--font-size-xs);
        padding: 2px 8px;
        border-radius: var(--border-radius-sm);
        text-transform: uppercase;
        font-weight: 500;
        flex-shrink: 0;
      }

      .zag-location-list-row__type-badge--physical {
        background-color: var(--color-success-light);
        color: var(--color-success-dark);
      }

      .zag-location-list-row__type-badge--virtual {
        background-color: var(--color-info-light);
        color: var(--color-info-dark);
      }

      .zag-location-list-row__description {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-location-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .zag-location-list-row__action {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-decoration: none;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          &:hover {
            background-color: transparent;
            color: var(--text-color-secondary);
          }
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-location-list-row__action--delete {
        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }
      }
    `,
  ],
})
export class LocationListRow {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The location data to display */
  readonly location = input.required<LocationListRowData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Whether to show the delete button */
  readonly showDelete = input(true);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the row is clicked */
  readonly rowClick = output<LocationListRowData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<LocationListRowData>();

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.location());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.location());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
